import { useMutation } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import { Alert } from "components/base/Toast";
import { ProductImagesV2 } from "components/shared/ProductImages";
import moment from "moment";
import QcStatus from "pages/qc/components/QcStatus";
import { FiEdit2, FiEye, FiTrash2 } from "react-icons/fi";
import { IoMdCheckmarkCircle } from "react-icons/io";
import { Link } from "react-router-dom";
import { timeSince } from "utils/date";
import { DELETE_QC_REPORT } from "../graphql";

const ReportField = ({ label, children }) => (
  <div className="my-1 text-sm flex items-start gap-2">
    <span className="w-24 flex-shrink-0 text-gray-600 dark:text-gray-400">
      {label}:
    </span>
    <span className="min-w-0 text-gray-700 dark:text-gray-200 break-words">
      {children}
    </span>
  </div>
);

const QcReportListRow = ({ report, sourceType = "SO" }) => {
  const [deleteReport, { loading }] = useMutation(DELETE_QC_REPORT, {
    onCompleted() {
      Alert("success", "Report Deleted.");
    },
    onError: (error) => {
      Alert("error", parseError(error));
    },
    awaitRefetchQueries: true,
    refetchQueries: ["FETCH_QC_REPORTS_LIST"],
  });

  const sourceNumber = report.transfer || report.so || "";
  const sourceLabel = report.transfer ? "Transfer" : "SO";
  const poNumbers = report.po ? [report.po] : [];
  const supplierName =
    report.snapshotSuppliers?.length > 0
      ? report.snapshotSuppliers.map((s) => s.nickName || s.name).join(", ")
      : "-";
  const customerText = report.customer?.name || "-";
  const packingName =
    report.factory?.name ?? report.product.packingFactory?.name ?? "-";
  const reportPathPrefix = sourceType === "PO" ? "po-reports" : "reports";
  const createdAtDate = report.createdAt ? new Date(report.createdAt) : null;
  const isRecentCreatedAt =
    !!createdAtDate &&
    Date.now() - createdAtDate.getTime() < 24 * 60 * 60 * 1000;
  const createdAtText = createdAtDate
    ? isRecentCreatedAt
      ? `${timeSince(createdAtDate)} ago`
      : moment(createdAtDate).format("YYYY-MM-DD HH:mm")
    : "-";
  const from = encodeURIComponent(
    window.location.pathname + window.location.search,
  );

  return (
    <div className="py-2 lg:py-4 border-gray-100 dark:border-gray-700 lg:flex justify-between items-start">
      <div>
        <div className="flex mb-1">
          <ProductImagesV2 images={report.product.images} size="w-12 h-12" />
        </div>
        <div className="text-base flex flex-wrap items-center gap-x-4">
          <span className="font-bold">
            [{report.product.number}] {report.product.name}
          </span>
          <span className="font-bold">✕ {report.qty}</span>
        </div>
        <ReportField label="Suppliers">{supplierName}</ReportField>
        {sourceType !== "PO" ? (
          <ReportField label="Packing">{packingName}</ReportField>
        ) : null}
        {sourceType === "PO" ? (
          <ReportField label="PO">
            {poNumbers.length > 0 ? poNumbers.join(", ") : "-"}
          </ReportField>
        ) : (
          <ReportField label={sourceLabel}>{sourceNumber || "-"}</ReportField>
        )}
        <ReportField label="Customer">{customerText}</ReportField>
        {report.sharedWithCustomerAt && (
          <div className="mt-2 text-purple-600 flex items-center space-x-2">
            <label>Shared with Customer</label>
            <IoMdCheckmarkCircle size={21} />
          </div>
        )}
      </div>

      <div className="mt-4 lg:mt-0 flex flex-col items-start lg:items-end self-stretch justify-between">
        <div>
          <div className="flex justify-start lg:justify-end">
            <QcStatus className="justify-end" status={report.status} />
          </div>
          <div className="mt-1 text-left lg:text-right text-gray-600 text-xs">
            {createdAtText}
          </div>
          <div className="text-left lg:text-right text-gray-600 text-xs">
            {report.user.email}
          </div>
        </div>

        <div className="mt-4 flex flex-col items-start lg:items-end whitespace-no-wrap">
          <div className="flex items-center gap-4">
            <Link
              className="inline-flex items-center gap-1"
              to={`/qc/${reportPathPrefix}/${report.id}?from=${from}`}
            >
              <FiEdit2 size={14} />
              <span>Edit</span>
            </Link>
            {sourceType !== "PO" ? (
              <Link
                className="inline-flex items-center gap-1"
                to={`/qc/${reportPathPrefix}/${report.id}/preview?from=${from}`}
              >
                <FiEye size={14} />
                <span>Preview</span>
              </Link>
            ) : null}
          </div>

          {report.deletedAt === null && (
            <div className="flex justify-start lg:justify-end mt-3">
              <Button
                danger
                loading={loading}
                onClick={(_) => {
                  if (window.confirm("Are you sure to delete this report?"))
                    deleteReport({ variables: { id: report.id } });
                }}
              >
                <span className="inline-flex items-center gap-1">
                  <FiTrash2 size={14} />
                  <span>Delete</span>
                </span>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QcReportListRow;
